import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import './IncluirPosicaoFinal.css';

/**
 * Página para Admin lançar posição final dos jogadores
 */

const IncluirPosicaoFinal = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [rodadaAberta, setRodadaAberta] = useState(null);
  const [jogadoresConfirmados, setJogadoresConfirmados] = useState([]);
  const [posicoesLancadas, setPosicoesLancadas] = useState([]); // Array de posições já lançadas
  const [jogadorSelecionado, setJogadorSelecionado] = useState('');
  const [posicao, setPosicao] = useState('');
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    carregarDados();
  }, [isAdmin, navigate]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      setError('');

      const rodadasRes = await apiClient.get('/rodadas');
      const rodadas = Array.isArray(rodadasRes.data) ? rodadasRes.data : [];
      const aberta = rodadas.find(r => r.status === 'aberta');

      if (!aberta) {
        setError('Não há rodada aberta. Abra uma rodada primeiro.');
        setLoading(false);
        return;
      }

      setRodadaAberta(aberta);

      // Carregar confirmações
      const confirmacoesRes = await apiClient.get(`/rodadas/${aberta.id}/confirmacoes`);
      const confirmacoes = Array.isArray(confirmacoesRes.data) ? confirmacoesRes.data : [];
      // Filtrar apenas jogadores sem posição definida
      const jogadoresSemPosicao = confirmacoes.filter(c => !c.posicao_final);
      setJogadoresConfirmados(jogadoresSemPosicao);
      
      // Carregar posições já lançadas para validação
      const posicoesJaLancadas = confirmacoes
        .filter(c => c.posicao_final)
        .map(c => c.posicao_final);
      setPosicoesLancadas(posicoesJaLancadas);

    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError(err.response?.data?.error || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleJogadorChange = (jogadorId) => {
    setJogadorSelecionado(jogadorId);
    setPosicao('');
    setError('');
    setSuccess('');
  };

  const handleSalvarPosicao = async (e) => {
    e.preventDefault();

    if (!jogadorSelecionado) {
      setError('Selecione um jogador');
      return;
    }

    if (!posicao || posicao === '') {
      setError('Informe a posição final');
      return;
    }

    const posicaoNum = parseInt(posicao);
    
    // Buscar todas as participações para validar range
    const todasParticipacoes = await apiClient.get(`/rodadas/${rodadaAberta.id}/confirmacoes`);
    const totalJogadores = Array.isArray(todasParticipacoes.data) ? todasParticipacoes.data.length : jogadoresConfirmados.length;
    
    // Validar: posição deve estar entre 1 e número de jogadores
    if (posicaoNum < 1 || posicaoNum > totalJogadores) {
      setError(`Posição deve estar entre 1 e ${totalJogadores}`);
      return;
    }

    // Validar: não pode ter posição duplicada
    if (posicoesLancadas.includes(posicaoNum)) {
      setError(`⚠️ A posição ${posicaoNum} já foi lançada para outro jogador. Por favor, escolha outra posição.`);
      return;
    }

    // Alerta: recomendar lançar do último para o primeiro
    const posicoesDisponiveis = [];
    for (let i = totalJogadores; i >= 1; i--) {
      if (!posicoesLancadas.includes(i)) {
        posicoesDisponiveis.push(i);
      }
    }
    
    if (posicoesDisponiveis.length > 0 && posicaoNum !== posicoesDisponiveis[0]) {
      const proximaPosicaoRecomendada = posicoesDisponiveis[0];
      if (!window.confirm(
        `💡 Recomendação: Para facilitar o lançamento, recomendamos lançar as posições do último para o primeiro.\n\n` +
        `Próxima posição recomendada: ${proximaPosicaoRecomendada}\n` +
        `Você está lançando: ${posicaoNum}\n\n` +
        `Deseja continuar mesmo assim?`
      )) {
        return;
      }
    }

    try {
      setSalvando(true);
      setError('');
      setSuccess('');

      await apiClient.put(`/rodadas/${rodadaAberta.id}/posicoes-finais`, {
        participacoes: [{
          jogador_id: jogadorSelecionado,
          posicao_final: posicaoNum,
        }],
      });

      const jogadorNome = jogadoresConfirmados.find(j => j.jogador_id === jogadorSelecionado)?.jogador?.nome || 'Jogador';
      setSuccess(`Posição ${posicaoNum} lançada para ${jogadorNome}! A pontuação já está sendo calculada no ranking.`);
      
      // Limpar formulário e recarregar
      setJogadorSelecionado('');
      setPosicao('');
      await carregarDados();
      
      // Atualizar lista de posições lançadas
      setPosicoesLancadas([...posicoesLancadas, posicaoNum]);
    } catch (err) {
      console.error('Erro ao salvar posição:', err);
      setError(err.response?.data?.error || 'Erro ao salvar posição');
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return <div className="container">Carregando...</div>;
  }

  if (!rodadaAberta) {
    return (
      <div className="container">
        <div className="error">Não há rodada aberta. Abra uma rodada primeiro.</div>
        <button onClick={() => navigate('/rodadas')} className="btn btn-primary">
          Ir para Rodadas
        </button>
      </div>
    );
  }

  return (
    <div className="container incluir-posicao-final-container">
      <div className="page-header">
        <h1>🏆 Lançar Posição Final</h1>
        <button onClick={() => navigate('/admin/menu')} className="btn btn-secondary">
          ← Voltar
        </button>
      </div>

      <div className="rodada-info">
        <p><strong>Rodada:</strong> {new Date(rodadaAberta.data).toLocaleDateString('pt-BR')}</p>
        <p><strong>Jogadores sem Posição:</strong> {jogadoresConfirmados.length}</p>
        {posicoesLancadas.length > 0 && (
          <p><strong>Posições já lançadas:</strong> {posicoesLancadas.sort((a, b) => b - a).join(', ')}</p>
        )}
        <div className="info-box" style={{ marginTop: '10px', padding: '10px', background: '#fff3cd', borderLeft: '4px solid #ffc107' }}>
          <p style={{ margin: 0, fontSize: '0.9em' }}>
            <strong>💡 Dica:</strong> Recomendamos lançar as posições do último para o primeiro (ex: 10, 9, 8...)
          </p>
        </div>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <form onSubmit={handleSalvarPosicao} className="posicao-form">
        <h3>Lançar Posição Final</h3>
        
        <div className="form-group">
          <label>Selecione o Jogador *</label>
          <select
            value={jogadorSelecionado}
            onChange={(e) => handleJogadorChange(e.target.value)}
            className="form-select"
            required
          >
            <option value="">-- Selecione um jogador --</option>
            {jogadoresConfirmados.map(jogador => (
              <option key={jogador.jogador_id} value={jogador.jogador_id}>
                {jogador.jogador?.nome || 'Jogador'}
              </option>
            ))}
          </select>
        </div>

        {jogadorSelecionado && (
          <div className="form-group">
            <label>Posição Final *</label>
            <input
              type="number"
              min="1"
              value={posicao}
              onChange={(e) => setPosicao(e.target.value)}
              className="posicao-input"
              placeholder="Ex: 1, 2, 3..."
              required
              autoFocus
            />
            <small>Posição entre 1 e número total de jogadores confirmados</small>
          </div>
        )}

        <div className="form-actions">
          <button 
            type="submit" 
            disabled={salvando || !jogadorSelecionado || !posicao} 
            className="btn btn-primary"
          >
            {salvando ? 'Salvando...' : 'Salvar Posição'}
          </button>
        </div>
      </form>

      {/* Lista de posições já lançadas */}
      {jogadoresConfirmados.length === 0 && (
        <div className="info-box success">
          <p><strong>✅ Todas as posições foram lançadas!</strong></p>
          <p>A rodada está pronta para ser fechada.</p>
        </div>
      )}

      <div className="info-box">
        <p>
          <strong>ℹ️ Informação:</strong> Ao salvar as posições, a pontuação será calculada automaticamente 
          e aparecerá no ranking geral. Quando fechar a rodada, essas posições serão utilizadas.
        </p>
      </div>
    </div>
  );
};

export default IncluirPosicaoFinal;
